'use client';

import { useEffect } from 'react';
import Header from '@/components/Header';
import GrievanceOfficerContact from '@/components/GrievanceOfficerContact';
import ContentWithShortcodes, {
  hasGrievanceOfficerShortcode,
} from '@/components/ContentWithShortcodes';
import { PrivacyPolicyData } from '@/services/privacyService';

interface TermsOfServiceClientProps {
  termsData: PrivacyPolicyData;
}

export default function TermsOfServiceClient({ termsData }: TermsOfServiceClientProps) {
  // Generate webpage schema with FAQs if present
  useEffect(() => {
    const generateWebPageSchema = () => {
      const baseSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": termsData.title,
        "description": termsData.seoDescription || termsData.metaDescription,
        "url": `${process.env.NEXT_PUBLIC_SITE_URL || 'https://marathivadhuvarsuchak.com'}/terms-and-conditions`,
        "datePublished": termsData.createdAt,
        "dateModified": termsData.updatedAt,
        "publisher": {
          "@type": "Organization",
          "name": "Marathi Vadhu Var Suchak",
          "url": process.env.NEXT_PUBLIC_SITE_URL || 'https://marathivadhuvarsuchak.com'
        }
      };

      // Add FAQ schema if FAQs are present
      if (termsData.faqs && termsData.faqs.length > 0) {
        const faqSchema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": termsData.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        };

        // Add both schemas to the page
        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.textContent = JSON.stringify([baseSchema, faqSchema]);
        document.head.appendChild(schemaScript);
      } else {
        // Add only the webpage schema
        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.textContent = JSON.stringify(baseSchema);
        document.head.appendChild(schemaScript);
      }
    };

    generateWebPageSchema();

    // Cleanup function to remove schema when component unmounts
    return () => {
      const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
      existingSchemas.forEach(schema => {
        if (schema.textContent?.includes(termsData.title)) {
          schema.remove();
        }
      });
    };
  }, [termsData]);

  return (
    <div className="bg-white">
      {/* Header */}
      <Header variant="fixed" />

      {/* Content Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* <h1 className="text-4xl font-bold text-gray-900 mb-8">
              {termsData.title}
            </h1> */}
            
            <div className="prose prose-lg max-w-none">
              {/* <p className="text-gray-600 mb-6">
                Last updated: {new Date(termsData.updatedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p> */}

              {/* Render the content from API */}
              <article>
                <ContentWithShortcodes html={termsData.content} />
              </article>

              {/* Render FAQs if present */}
              {termsData.faqs && termsData.faqs.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {termsData.faqs.map((faq) => (
                      <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200">
                          <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                        </div>
                        <div className="px-6 py-4 bg-white border-t border-gray-200">
                          <div 
                            className="content-prose-sm"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasGrievanceOfficerShortcode(termsData.content) && <GrievanceOfficerContact />}
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 info-box">
                <p className="text-blue-800 text-sm leading-relaxed">
                  By using our platform, you acknowledge that you have read and understood these Terms & Conditions and agree to be bound by them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
